import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Clock3,
  MessageCircle,
  Paperclip,
  Play,
  Save,
  Send,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  formatFileSize,
  formatItDateTime,
  formatItTime,
  getItDrawerActions,
  getItStateLabel,
  type ItAttachment,
  type ItComment,
  type ItIncident,
  type ItMeta,
} from "../types/it";

type Props = {
  incident: ItIncident | null;
  meta: ItMeta;
  busy: boolean;
  open: boolean;
  onClose: () => void;
  onApprove: (incident: ItIncident) => Promise<void>;
  onRejectRequest: (incident: ItIncident) => Promise<void>;
  onAssign: (incident: ItIncident, responsableId: number) => Promise<void>;
  onStart: (incident: ItIncident) => Promise<void>;
  onFinish: (incident: ItIncident, tiempoDedicado: string) => Promise<void>;
  onValidate: (
    incident: ItIncident,
    action: "aceptar" | "rechazar",
  ) => Promise<void>;
  onReopen: (incident: ItIncident) => Promise<void>;
  onSaveObservation: (
    incident: ItIncident,
    type: "solicitante" | "responsable",
    value: string,
  ) => Promise<void>;
  onSendComment: (incident: ItIncident, value: string) => Promise<void>;
};

export function ItDetailDrawer({
  incident,
  meta,
  busy,
  open,
  onClose,
  onApprove,
  onRejectRequest,
  onAssign,
  onStart,
  onFinish,
  onValidate,
  onReopen,
  onSaveObservation,
  onSendComment,
}: Props) {
  const [requesterObservation, setRequesterObservation] = useState("");
  const [responsibleObservation, setResponsibleObservation] = useState("");
  const [comment, setComment] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [selectedTime, setSelectedTime] = useState("01:00:00");

  useEffect(() => {
    setRequesterObservation(incident?.observaciones ?? "");
    setResponsibleObservation(incident?.observaciones_responsable ?? "");
    setSelectedTechnician(
      incident?.responsable_id ? String(incident.responsable_id) : "",
    );
    setComment("");
  }, [
    incident?.id,
    incident?.observaciones,
    incident?.observaciones_responsable,
    incident?.responsable_id,
  ]);

  const actions = useMemo(
    () =>
      incident
        ? getItDrawerActions(incident, meta.current_user)
        : {
            canApproveRequest: false,
            canRejectRequest: false,
            canAssign: false,
            canStart: false,
            canFinish: false,
            canValidate: false,
            canReopen: false,
            canEditRequesterObservation: false,
            canEditResponsibleObservation: false,
            canComment: false,
            canUploadAttachments: false,
          },
    [incident, meta.current_user],
  );

  const comments = incident?.comentarios ?? [];
  const attachments = incident?.adjuntos_items ?? [];
  const history = incident?.historial ?? [];

  return (
    <>
      <div
        className={[
          "fixed inset-0 z-40 bg-white/35 backdrop-blur-[2px] transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
        onClick={onClose}
      />

      <div
        className={[
          "it-drawer fixed inset-y-0 right-0 z-50 w-full max-w-[920px] transform border-l border-white/60 bg-white/88 shadow-[0_44px_120px_rgba(15,23,42,.24)] backdrop-blur-2xl transition duration-300",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-full flex-col">
          <header className="border-b border-white/60 bg-white/[0.58] px-6 py-5 backdrop-blur-2xl">
            <div className="pointer-events-none absolute -right-24 top-[-120px] h-[280px] w-[280px] rounded-full bg-emerald-300/24 blur-3xl" />
            <div className="pointer-events-none absolute left-1/3 top-[-120px] h-[240px] w-[240px] rounded-full bg-teal-300/14 blur-3xl" />

            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/80 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.08)]">
                  {incident?.codigo ?? "Incidencia"}
                </div>

                <h2 className="mt-3 text-2xl font-black tracking-tight text-slate-950">
                  {incident?.titulo ?? "Detalle de incidencia"}
                </h2>

                <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                  {incident
                    ? getItStateLabel(incident.estado)
                    : "Selecciona una incidencia"}
                </p>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-rose-50 hover:text-rose-600"
              >
                <X size={17} />
              </button>
            </div>

            {incident && (
              <div className="relative mt-5 flex flex-wrap gap-2">
                {actions.canApproveRequest && (
                  <>
                    <ActionButton
                      icon={ShieldCheck}
                      text="Aprobar"
                      busy={busy}
                      onClick={() => void onApprove(incident)}
                    />

                    <ActionButton
                      icon={X}
                      text="Rechazar solicitud"
                      tone="danger"
                      busy={busy}
                      onClick={() => void onRejectRequest(incident)}
                    />
                  </>
                )}

                {actions.canAssign && (
                  <div className="flex flex-wrap items-center gap-2 rounded-full border border-[#dfe8e4] bg-white px-2 py-2 shadow-[0_10px_26px_rgba(15,23,42,.055)]">
                    <select
                      value={selectedTechnician}
                      onChange={(event) =>
                        setSelectedTechnician(event.target.value)
                      }
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
                    >
                      <option value="">Selecciona tÃ©cnico</option>
                      {meta.technicians.map((technician) => (
                        <option
                          key={technician.id}
                          value={String(technician.id)}
                        >
                          {technician.nombre}
                        </option>
                      ))}
                    </select>

                    <ActionButton
                      icon={Save}
                      text="Asignar"
                      busy={busy}
                      disabled={!selectedTechnician}
                      onClick={() =>
                        void onAssign(incident, Number(selectedTechnician))
                      }
                    />
                  </div>
                )}

                {actions.canStart && (
                  <ActionButton
                    icon={Play}
                    text="Iniciar"
                    busy={busy}
                    onClick={() => void onStart(incident)}
                  />
                )}

                {actions.canFinish && (
                  <div className="flex flex-wrap items-center gap-2 rounded-full border border-[#dfe8e4] bg-white px-2 py-2 shadow-[0_10px_26px_rgba(15,23,42,.055)]">
                    <select
                      value={selectedTime}
                      onChange={(event) => setSelectedTime(event.target.value)}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 outline-none transition focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
                    >
                      <option value="00:30:00">30 minutos</option>
                      <option value="01:00:00">1 hora</option>
                      <option value="02:00:00">2 horas</option>
                      <option value="03:00:00">3 horas</option>
                      <option value="04:00:00">4 horas</option>
                      <option value="06:00:00">6 horas</option>
                      <option value="08:00:00">8 horas</option>
                      <option value="16:00:00">2 dÃ­as</option>
                      <option value="24:00:00">3 dÃ­as</option>
                      <option value="40:00:00">5 dÃ­as</option>
                    </select>

                    <ActionButton
                      icon={Clock3}
                      text="Finalizar"
                      busy={busy}
                      onClick={() => void onFinish(incident, selectedTime)}
                    />
                  </div>
                )}

                {actions.canValidate && (
                  <>
                    <ActionButton
                      icon={CheckCircle2}
                      text="Aceptar correcciÃ³n"
                      busy={busy}
                      onClick={() => void onValidate(incident, "aceptar")}
                    />

                    <ActionButton
                      icon={X}
                      text="Rechazar correcciÃ³n"
                      tone="danger"
                      busy={busy}
                      onClick={() => void onValidate(incident, "rechazar")}
                    />
                  </>
                )}

                {actions.canReopen && (
                  <ActionButton
                    icon={Clock3}
                    text="Reabrir"
                    tone="warning"
                    busy={busy}
                    onClick={() => void onReopen(incident)}
                  />
                )}
              </div>
            )}
          </header>

          {incident ? (
            <div className="grid min-h-0 flex-1 gap-0 bg-white/[0.38] backdrop-blur-xl lg:grid-cols-[minmax(0,1fr)_290px]">
              <div className="it-detail-scroll space-y-5 overflow-y-auto px-6 py-5">
                <section className="surface-card p-5">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                    Descripción
                  </div>

                  <p className="mt-3 whitespace-pre-wrap text-sm font-semibold leading-7 text-slate-600">
                    {incident.descripcion || "Sin descripciÃ³n"}
                  </p>
                </section>

                <section className="surface-card p-5">
                  <div className="flex items-center gap-2 text-sm font-black text-slate-950">
                    <Save size={16} className="text-emerald-600" />
                    Observaciones
                  </div>

                  <div className="mt-4 grid gap-4 xl:grid-cols-2">
                    <ObservationEditor
                      title="Solicitante"
                      value={requesterObservation}
                      editable={actions.canEditRequesterObservation}
                      onChange={setRequesterObservation}
                      onSave={() =>
                        void onSaveObservation(
                          incident,
                          "solicitante",
                          requesterObservation,
                        )
                      }
                      busy={busy}
                    />

                    <ObservationEditor
                      title="Responsable"
                      value={responsibleObservation}
                      editable={actions.canEditResponsibleObservation}
                      onChange={setResponsibleObservation}
                      onSave={() =>
                        void onSaveObservation(
                          incident,
                          "responsable",
                          responsibleObservation,
                        )
                      }
                      busy={busy}
                    />
                  </div>
                </section>

                <section className="surface-card flex min-h-[340px] flex-col p-5">
                  <div className="flex items-center gap-2 text-sm font-black text-slate-950">
                    <MessageCircle size={16} className="text-emerald-600" />
                    Comentarios estilo realtime
                  </div>

                  <div className="dashboard-chat-scroll mt-4 flex-1 space-y-3 overflow-y-auto rounded-[26px] border border-[#edf1f3] bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,.06),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,.05),transparent_24%),#f8fbfc] p-4">
                    {comments.length === 0 && (
                      <div className="rounded-[22px] border border-dashed border-emerald-100 bg-white/80 p-4 text-center text-sm font-bold text-slate-400">
                        TodavÃ­a no hay comentarios en esta incidencia.
                      </div>
                    )}

                    {comments.map((item) => (
                      <CommentBubble key={item.id} comment={item} />
                    ))}
                  </div>

                  <div className="mt-4 flex items-center gap-3 rounded-[28px] border border-[#dfe8e4] bg-white p-3 shadow-[0_18px_44px_rgba(15,23,42,.08)] transition-all duration-300 focus-within:border-emerald-300 focus-within:ring-4 focus-within:ring-emerald-100">
                    <input
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      disabled={!actions.canComment}
                      onKeyDown={(event) => {
                        if (!actions.canComment) return;
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();

                          if (!comment.trim()) return;

                          void onSendComment(incident, comment.trim());
                          setComment("");
                        }
                      }}
                      className="min-w-0 flex-1 bg-transparent px-2 text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:text-slate-400"
                      placeholder={
                        actions.canComment
                          ? "Escribe un comentario..."
                          : "Solo solicitante, responsable o administradores pueden comentar."
                      }
                    />

                    <button
                      type="button"
                      disabled={busy || !comment.trim() || !actions.canComment}
                      onClick={() => {
                        if (!actions.canComment) return;
                        void onSendComment(incident, comment.trim());
                        setComment("");
                      }}
                      className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-[linear-gradient(135deg,#059669,#10b981,#14b8a6)] px-5 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_24px_52px_rgba(16,185,129,.30)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Send size={16} />
                      Enviar
                    </button>
                  </div>
                </section>

                <section className="surface-card p-5">
                  <div className="flex items-center gap-2 text-sm font-black text-slate-950">
                    <Paperclip size={16} className="text-emerald-600" />
                    Adjuntos
                  </div>

                  <div className="mt-4 grid gap-3">
                    {attachments.length === 0 && (
                      <div className="rounded-[22px] border border-dashed border-emerald-100 bg-emerald-50/45 p-4 text-center text-sm font-bold text-slate-400">
                        No hay adjuntos registrados.
                      </div>
                    )}

                    {attachments.slice(0, 1).map((item) => (
                      <AttachmentItem key={String(item.id)} attachment={item} />
                    ))}
                  </div>
                </section>
              </div>

              <aside className="it-detail-scroll border-t border-white/60 bg-white/[0.34] px-5 py-5 backdrop-blur-xl lg:border-l lg:border-t-0">
                <section className="surface-card p-5">
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                    Resumen
                  </div>

                  <div className="mt-4 space-y-3 text-sm font-semibold text-slate-600">
                    <SummaryRow
                      label="Proyecto"
                      value={incident.proyecto_nombre || "Sin proyecto"}
                    />
                    <SummaryRow
                      label="Estado"
                      value={getItStateLabel(incident.estado)}
                    />
                    <SummaryRow
                      label="Prioridad"
                      value={incident.prioridad.toUpperCase()}
                    />
                    <SummaryRow
                      label="CategorÃ­a"
                      value={incident.categoria || "â€”"}
                    />
                    <SummaryRow
                      label="Creador"
                      value={incident.usuario_alta_nombre || "â€”"}
                    />
                    <SummaryRow
                      label="Responsable"
                      value={incident.responsable_nombre || "Sin asignar"}
                    />
                    <SummaryRow
                      label="Reaperturas"
                      value={String(incident.veces_reabierta_cerrada)}
                    />
                    <SummaryRow
                      label="Alta"
                      value={formatItDateTime(incident.fecha_alta)}
                    />
                    <SummaryRow
                      label="Inicio"
                      value={formatItDateTime(incident.fecha_inicio)}
                    />
                    <SummaryRow
                      label="Fin trabajo"
                      value={formatItDateTime(incident.fecha_fin_trabajo)}
                    />
                    <SummaryRow
                      label="Cierre"
                      value={formatItDateTime(incident.fecha_cierre)}
                    />
                    <SummaryRow
                      label="Tiempo"
                      value={formatItTime(incident.tiempo_dedicado)}
                    />
                  </div>
                </section>

                <section className="surface-card mt-5 p-5">
                  <div className="text-sm font-black text-slate-950">
                    Historial lateral
                  </div>

                  <div className="mt-4 space-y-3">
                    {history.length === 0 && (
                      <div className="rounded-[22px] border border-dashed border-emerald-100 bg-emerald-50/45 p-4 text-center text-sm font-bold text-slate-400">
                        TodavÃ­a no hay eventos guardados.
                      </div>
                    )}

                    {history.map((item) => (
                      <div
                        key={item.id}
                        className="relative overflow-hidden rounded-[24px] border border-[#edf1f3] bg-white px-4 py-4 shadow-[0_10px_26px_rgba(15,23,42,.045)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-100 hover:bg-emerald-50/40"
                      >
                        <span className="absolute left-4 top-4 h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_0_5px_rgba(16,185,129,.12)]" />

                        <div className="pl-5">
                          <div className="text-sm font-black text-slate-800">
                            {item.usuario_nombre || "Usuario"}
                          </div>

                          <div className="text-[11px] font-black uppercase tracking-[0.16em] text-emerald-600">
                            {item.accion}
                          </div>

                          <div className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                            {item.descripcion || "Sin descripción adicional"}
                          </div>

                          <div className="mt-2 text-xs font-semibold text-slate-400">
                            {formatItDateTime(item.fecha)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </aside>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,.045),transparent_28%),#f8fbfc] px-8 text-center">
              <div className="max-w-md rounded-[34px] border border-dashed border-emerald-100 bg-white/80 p-10 text-slate-400 shadow-[0_24px_70px_rgba(15,23,42,.08)]">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700">
                  <MessageCircle size={22} />
                </div>

                <div className="mt-4 text-lg font-black text-slate-950">
                  Nada abierto todavÃ­a
                </div>

                <p className="mt-3 text-sm font-semibold leading-6">
                  Selecciona una incidencia del tablero para revisar estado,
                  historial, observaciones, comentarios y adjuntos.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ActionButton({
  icon: Icon,
  text,
  tone = "primary",
  disabled = false,
  busy = false,
  onClick,
}: {
  icon: typeof Play;
  text: string;
  tone?: "primary" | "danger" | "warning";
  disabled?: boolean;
  busy: boolean;
  onClick: () => void;
}) {
  const palette =
    tone === "danger"
      ? "border-rose-100 bg-rose-50 text-rose-600 hover:bg-rose-100"
      : tone === "warning"
        ? "border-amber-100 bg-amber-50 text-amber-700 hover:bg-amber-100"
        : "border-emerald-100 bg-emerald-50 text-emerald-700 hover:bg-emerald-100";

  return (
    <button
      type="button"
      disabled={busy || disabled}
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-black shadow-[0_8px_20px_rgba(15,23,42,.045)] transition-all duration-300 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50",
        palette,
      ].join(" ")}
    >
      <Icon size={15} />
      {text}
    </button>
  );
}

function ObservationEditor({
  title,
  value,
  editable,
  busy,
  onChange,
  onSave,
}: {
  title: string;
  value: string;
  editable: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
}) {
  return (
    <div className="rounded-[26px] border border-[#edf1f3] bg-white p-4 shadow-[0_10px_26px_rgba(15,23,42,.045)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-100">
      <div className="text-sm font-black text-slate-950">{title}</div>

      <textarea
        value={value}
        readOnly={!editable}
        onChange={(event) => onChange(event.target.value)}
        className={[
          "mt-3 min-h-[140px] w-full rounded-[22px] border px-4 py-3 text-sm font-semibold leading-6 outline-none transition-all duration-300",
          editable
            ? "border-[#dfe8e4] bg-white text-slate-700 focus:border-emerald-300 focus:ring-4 focus:ring-emerald-100"
            : "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400",
        ].join(" ")}
      />

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          disabled={busy || !editable}
          onClick={onSave}
          className="rounded-full bg-slate-950 px-4 py-2 text-xs font-black text-white shadow-[0_12px_28px_rgba(15,23,42,.18)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Guardar
        </button>
      </div>
    </div>
  );
}

function CommentBubble({ comment }: { comment: ItComment }) {
  const isMine = comment.mine;

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
      <div
        className={[
          "max-w-[78%] rounded-[24px] px-4 py-3 text-sm font-semibold leading-6 shadow-[0_10px_28px_rgba(15,23,42,.05)] transition-all duration-300 hover:-translate-y-0.5",
          isMine
            ? "border border-emerald-200 bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_14px_34px_rgba(16,185,129,.16)]"
            : "border border-[#e7ecef] bg-white text-slate-700",
        ].join(" ")}
      >
        <div
          className={[
            "text-[11px] font-black uppercase tracking-[0.16em]",
            isMine ? "text-white/70" : "text-emerald-600",
          ].join(" ")}
        >
          {comment.usuario_nombre || "Usuario"}
        </div>

        <div className="mt-1">{comment.mensaje}</div>

        <div
          className={[
            "mt-2 text-[11px] font-bold",
            isMine ? "text-white/70" : "text-slate-400",
          ].join(" ")}
        >
          {formatItDateTime(comment.creado_en)}
        </div>
      </div>
    </div>
  );
}

function AttachmentItem({ attachment }: { attachment: ItAttachment }) {
  const isImage =
    attachment.archivo_mime?.startsWith("image/") ||
    /\.(png|jpe?g|webp|gif)$/i.test(attachment.archivo_nombre);

  return (
    <a
      href={attachment.download_url}
      target="_blank"
      rel="noreferrer"
      className="group overflow-hidden rounded-[24px] border border-[#edf1f3] bg-white shadow-[0_10px_26px_rgba(15,23,42,.045)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-100 hover:bg-emerald-50/40"
    >
      {isImage && (
        <div className="border-b border-[#edf1f3] bg-slate-50">
          <img
            src={attachment.download_url}
            alt={attachment.archivo_nombre}
            className="h-44 w-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      <div className="flex items-center justify-between gap-3 px-4 py-4">
        <div className="min-w-0">
          <div className="truncate text-sm font-black text-slate-800">
            {attachment.archivo_nombre}
          </div>

          <div className="mt-1 text-xs font-semibold text-slate-500">
            {formatFileSize(attachment.archivo_size)} Â·{" "}
            {attachment.usuario_nombre || "Usuario"}
          </div>
        </div>

        <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700 transition-all duration-300 group-hover:bg-emerald-100">
          Descargar
        </span>
      </div>
    </a>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[20px] border border-[#edf1f3] bg-white px-4 py-3 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-100 hover:bg-emerald-50/35">
      <div className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </div>

      <div className="mt-1 text-sm font-black text-slate-800">{value}</div>
    </div>
  );
}
